import React, {Key} from 'react';
import {Tag, Col, Row} from 'antd';
import styles from './index.module.scss';
import useMergedState from 'rc-util/lib/hooks/useMergedState';

export type ValueType = Key | Key[] | undefined;

interface TagData {
    value: Key;
    label: string;
}

interface BaseProps {
    label: string;
    labelWidth?: number;
    tagData: TagData[];
    multiple?: boolean;
    hideAll?: boolean; // 隐藏'全部'选项
}

export interface FormFilterInnerProps<T extends ValueType> extends BaseProps {
    value?: T;
    defaultValue?: T;
    onChange?: (key: T) => void;
}

const FormFilterInner = ({
    label,
    labelWidth,
    tagData,
    defaultValue,
    onChange,
    value,
    multiple,
    hideAll,
}: FormFilterInnerProps<ValueType>) => {
    const [innerValue, setInnerValue] = useMergedState(
        defaultValue || (multiple ? [] : undefined),
        {
            value,
            onChange,
        },
    );

    function isChecked(key?: Key) {
        if (key === undefined) {
            return (
                innerValue === undefined ||
                (Array.isArray(innerValue) && innerValue.length === 0)
            );
        }

        if (Array.isArray(innerValue)) {
            return innerValue.includes(key);
        } else {
            return innerValue === key;
        }
    }

    function onCheck(key: Key) {
        if (Array.isArray(innerValue)) {
            if (innerValue.includes(key)) {
                setInnerValue(innerValue.filter(item => item !== key));
            } else {
                setInnerValue([...innerValue, key]);
            }
        } else {
            setInnerValue(key);
        }
    }

    function onCheckAll() {
        if (multiple) {
            setInnerValue([]);
        } else {
            setInnerValue(undefined);
        }
    }

    return (
        <Row className={styles.root}>
            <Col className={styles.label} style={{width: labelWidth}}>
                {label}：
            </Col>

            <Col className={styles.tags}>
                {!hideAll && (
                    <Tag.CheckableTag
                        checked={isChecked(undefined)}
                        onClick={onCheckAll}>
                        全部
                    </Tag.CheckableTag>
                )}

                {tagData.map(item => (
                    <Tag.CheckableTag
                        key={item.value}
                        checked={isChecked(item.value)}
                        onClick={() => onCheck(item.value)}>
                        {item.label}
                    </Tag.CheckableTag>
                ))}
            </Col>
        </Row>
    );
};

FormFilterInner.defaultProps = {
    labelWidth: 80,
    multiple: false,
    hideAll: false,
};

export default FormFilterInner as <T extends ValueType>(
    props: FormFilterInnerProps<T>,
) => React.ReactElement;
